import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2, Mail, MapPin, Send } from "lucide-react";
import Seo from "../Components/Seo";
import PageHero from "../Components/PageHero";
import { SITE } from "../data/site";

const INITIAL = { name: "", email: "", company: "", phone: "", details: "" };

const ContactPage = () => {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const next = {};
    if (!form.name.trim()) next.name = "Please enter your name.";
    if (!form.email.trim()) {
      next.email = "Please enter your email.";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Please enter a valid email address.";
    }
    if (!form.details.trim()) next.details = "Please tell us about your project.";
    return next;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const next = validate();
    if (Object.keys(next).length) {
      setErrors(next);
      return;
    }
    // No backend wired yet — show success state. Hook up to an email
    // service / API endpoint here when available.
    setSubmitted(true);
    setForm(INITIAL);
  };

  const fields = [
    { name: "name", label: "Name", type: "text", required: true, full: false },
    { name: "email", label: "Email", type: "email", required: true, full: false },
    { name: "company", label: "Company", type: "text", required: false, full: false },
    { name: "phone", label: "Phone", type: "tel", required: false, full: false },
  ];

  return (
    <>
      <Seo
        title="Contact Triguna Solutions | Let's Build Something Extraordinary"
        description="Connect with Triguna Solutions' technology experts to discuss your AI, software, cloud, or data engineering project. Schedule a free consultation today."
        path="/contact"
      />

      <PageHero
        eyebrow="Contact Us"
        title="Let's Build Something Extraordinary"
        subtitle="Connect with our technology experts to discuss your next project. We'll get back to you within one business day."
      />

      <section className="px-4 py-20 sm:px-6 sm:py-24 lg:px-8">
        <div className="mx-auto grid max-w-6xl gap-12 lg:grid-cols-2">
          {/* Intro */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display text-2xl font-bold text-foreground">
              Reach us directly
            </h2>
            <p className="mt-3 text-muted-foreground">
              Prefer email? Drop us a line and our team will follow up promptly.
            </p>

            <div className="mt-8 space-y-5">
              <a
                href={`mailto:${SITE.email}`}
                className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition-colors hover:border-primary/40"
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white">
                  <Mail className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm text-muted-foreground">Email</span>
                  <span className="block font-medium text-foreground">{SITE.email}</span>
                </span>
              </a>
              <div className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-brand-gradient text-white">
                  <MapPin className="h-5 w-5" />
                </span>
                <span>
                  <span className="block text-sm text-muted-foreground">Location</span>
                  <span className="block font-medium text-foreground">{SITE.location}</span>
                </span>
              </div>
            </div>
          </motion.div>

          {/* Form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="rounded-3xl border border-border bg-card p-6 shadow-xl sm:p-8"
          >
            {submitted ? (
              <div className="flex h-full flex-col items-center justify-center py-12 text-center">
                <CheckCircle2 className="h-14 w-14 text-success" />
                <h2 className="mt-4 font-display text-2xl font-bold text-foreground">
                  Thank you!
                </h2>
                <p className="mt-2 max-w-sm text-muted-foreground">
                  Your message has been received. Our team will reach out to you
                  shortly to schedule your consultation.
                </p>
                <button
                  onClick={() => setSubmitted(false)}
                  className="mt-6 rounded-full border border-border px-6 py-2.5 text-sm font-semibold text-foreground transition-colors hover:bg-muted"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} noValidate className="space-y-5">
                <div className="grid gap-5 sm:grid-cols-2">
                  {fields.map((field) => (
                    <div key={field.name}>
                      <label
                        htmlFor={field.name}
                        className="mb-1.5 block text-sm font-medium text-foreground"
                      >
                        {field.label}
                        {field.required && <span className="text-destructive"> *</span>}
                      </label>
                      <input
                        id={field.name}
                        name={field.name}
                        type={field.type}
                        value={form[field.name]}
                        onChange={handleChange}
                        className={`w-full rounded-xl border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                          errors[field.name] ? "border-destructive" : "border-border"
                        }`}
                        placeholder={field.label}
                      />
                      {errors[field.name] && (
                        <p className="mt-1 text-xs text-destructive">{errors[field.name]}</p>
                      )}
                    </div>
                  ))}
                </div>

                <div>
                  <label
                    htmlFor="details"
                    className="mb-1.5 block text-sm font-medium text-foreground"
                  >
                    Project Details<span className="text-destructive"> *</span>
                  </label>
                  <textarea
                    id="details"
                    name="details"
                    rows={5}
                    value={form.details}
                    onChange={handleChange}
                    className={`w-full rounded-xl border bg-background px-4 py-3 text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 ${
                      errors.details ? "border-destructive" : "border-border"
                    }`}
                    placeholder="Tell us about your project, goals, and timeline."
                  />
                  {errors.details && (
                    <p className="mt-1 text-xs text-destructive">{errors.details}</p>
                  )}
                </div>

                <motion.button
                  type="submit"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-brand-gradient px-7 py-3.5 text-base font-semibold text-white shadow-lg shadow-primary/30"
                >
                  Schedule Consultation
                  <Send className="h-4 w-4" />
                </motion.button>
              </form>
            )}
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default ContactPage;
